import { useEffect, useState, ReactNode, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Global subscription validation handler
const handleSubscriptionError = (navigate: ReturnType<typeof useNavigate>) => {
    return (response: Response) => {
        if (response.status === 403) {
            response
                .json()
                .then((data) => {
                    if (data.error === 'Active subscription required') {
                        navigate('/app/settings/billing', { replace: true });
                    }
                })
                .catch(() => {
                    // If JSON parsing fails, still redirect to billing
                    navigate('/app/settings/billing', { replace: true });
                });
        }
    };
};

interface RequireSubscriptionProps {
    children: ReactNode;
}

const RequireSubscription = ({ children }: RequireSubscriptionProps) => {
    const [checkingSubscription, setCheckingSubscription] = useState(false);
    const [hasSubscription, setHasSubscription] = useState(false);
    const navigate = useNavigate();
    const { session, loading, isSubAccount } = useAuth();
    const hasChecked = useRef(false);

    // SECURITY NOTE: This client-side validation is for UX only.
    // Server-side middleware on all protected API routes is the actual security enforcement.
    // Users can bypass this component, but they cannot bypass server-side validation.

    useEffect(() => {
        const checkSubscription = async () => {
            // Sub-accounts don't need subscription checks - they inherit from main account
            if (isSubAccount) {
                setHasSubscription(true);
                hasChecked.current = true;
                return;
            }

            // Don't check if auth is still loading or no session
            if (loading || !session?.access_token) {
                return;
            }

            // Don't re-check if we already checked successfully
            if (hasChecked.current) {
                return;
            }

            setCheckingSubscription(true);
            try {
                // Use the same API pattern as your other components
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${session.access_token}`,
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error('Failed to check subscription');
                }

                const studio = await response.json();

                // Check if the studio has an active subscription
                const hasActiveSubscription = !!studio.is_active;
                setHasSubscription(hasActiveSubscription);

                if (!hasActiveSubscription) {
                    // Redirect to inactive studio billing flow instead of onboarding
                    navigate('/app/inactive-studio/settings/billing', { replace: true });
                    return;
                }

                hasChecked.current = true;
            } catch (error) {
                // Handle server-side subscription validation failures
                if (error instanceof Error && error.message.includes('403')) {
                    navigate('/app/inactive-studio/settings/billing', { replace: true });
                } else {
                    // For other errors, redirect to onboarding
                    navigate('/app/onboarding');
                }
            } finally {
                setCheckingSubscription(false);
            }
        };

        checkSubscription();
    }, [session, loading, navigate, isSubAccount]);

    // Show loading only during the subscription check, not during auth loading
    if (checkingSubscription) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    // Allow rendering children if user has subscription
    return hasSubscription ? <>{children}</> : null;
};

export default RequireSubscription;
